/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  SkipSelf,
} from '@angular/core';

import { domOffsetUntil } from '@gh/core-util';
import { distinctUntilChanged$ } from '@gh/rx/operators';

import { isEqual, isNil } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  EMPTY_BOX,
  VIRTUAL_CONTAINER_PROVIDER,
  VirtualContainer,
  VirtualContainerProvider,
  VirtualPosition,
} from './virtual-rendering.interfaces';

export type VirtualListOrientation = 'horizontal' | 'vertical';

/**
 * {@link VirtualListComponent} renders virtual items built via <code>*ghVirtualListFor</code> directive.
 */
@Component({
  selector: 'gh-virtual-list',
  template: `
    <ng-content></ng-content>`,
  styles: [require('./virtual-list.component.scss')],
  providers: [{ provide: VIRTUAL_CONTAINER_PROVIDER, useExisting: VirtualListComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualListComponent implements OnInit, OnDestroy, VirtualContainerProvider {
  container: Observable<VirtualContainer>;

  @Input() orientation: VirtualListOrientation = 'vertical';

  private lastDimension: number = NaN;
  private _container$$ = new BehaviorSubject<VirtualContainer>(EMPTY_BOX);

  get containerSnapshot(): VirtualContainer {
    return this._container$$.value;
  }

  constructor(private elementRef: ElementRef,
              private renderer: Renderer2,
              @SkipSelf() @Inject(VIRTUAL_CONTAINER_PROVIDER) private parentContainer: VirtualContainerProvider) {
  }

  ngOnInit(): void {
    this.container = this._container$$.pipe(distinctUntilChanged$(isEqual));

    this.fireContainerChange();
  }

  ngOnDestroy(): void {
    this._container$$.complete();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.fireContainerChange();
  }

  setDimensions(width: number, height: number): void {
    if (this.orientation === 'vertical' && this.lastDimension !== height) {
      this.renderer.setStyle(this.elementRef.nativeElement, 'height', `${height}px`);

      this.lastDimension = height;
    } else if (this.orientation === 'horizontal' && this.lastDimension !== width) {
      this.renderer.setStyle(this.elementRef.nativeElement, 'width', `${width}px`);

      this.lastDimension = width;
    }
    this.fireContainerChange();
  }

  calculateOffset(element: HTMLElement): VirtualPosition {
    const offset = domOffsetUntil(element, this.elementRef.nativeElement);

    if (isNil(offset)) {
      throw new Error('VirtualListComponent: cannot find offset for the provided element');
    }

    return offset;
  }

  private fireContainerChange(): void {
    const containerEl = this.elementRef.nativeElement;

    const { left, top } = this.parentContainer.calculateOffset(containerEl);

    this._container$$.next({
      left,
      top,
      right: left + containerEl.offsetWidth,
      bottom: top + containerEl.offsetHeight,
    });
  }
}
